import { useEffect, useMemo, useState } from "react";
import ProductCard from "../../../components/ProductCard";
import ProductCardSkeleton from "../../../components/ProductCardSkeleton";
import SliderComp from "../../../components/SliderComp";
import Tabs from "../../../components/Tabs";
import API from "../../../utils/api";
import { filterVisibleCategories } from "../../../utils/categoryFilters";
import classes from "./Section3.module.css";

const getImageUrl = (imageName) => {
  const baseUrl = API.base.endsWith("/techmatter_api")
    ? API.base
    : `${API.base}/techmatter_api`;

  return imageName
    ? `${baseUrl}/upload/${imageName}`
    : `${baseUrl}/upload/placeholder-image.jpg`;
};

export default function Section3() {
  const [categories, setCategories] = useState([]);
  const [productsData, setProductsData] = useState({});
  const [selectedCategory, setSelectedCategory] = useState("");
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState({}); // per-category loading

  useEffect(() => {
    async function fetchCategories() {
      setLoadingCategories(true);
      try {
        const res = await API.post("/categories.php", { action: "fetch" });
        const cats = Array.isArray(res.data) ? res.data : res.data?.data || [];
        const visibleCats = filterVisibleCategories(cats).slice(0, 3); // optional limit to 3 categories
        const catMap = visibleCats.map((cat) => ({
          label: cat.categoryName,
          value: cat.category_slug,
        }));

        setCategories(catMap);
        setSelectedCategory(catMap[0]?.value || "");

        // Fetch 5 products per category
        const productFetchPromises = catMap.map(async (cat) => {
          setLoadingProducts((prev) => ({ ...prev, [cat.value]: true }));
          try {
            const params = new URLSearchParams({
              action: "fetch",
              category_slug: cat.value,
              limit: "5",
              status: "publish",
            });
            const resp = await fetch(`${API.base}/products.php`, {
              method: "POST",
              headers: { "Content-Type": "application/x-www-form-urlencoded" },
              body: params,
            });
            const data = await resp.json();
            const products = Array.isArray(data) ? data : data.data || [];
            const mapped = products.map((p) => ({
              ...p,
              productImage: getImageUrl(p.productImage),
              regularPrice: parseFloat(p.regularPrice) || 0,
              salePrice: parseFloat(p.salePrice) || 0,
            }));
            setProductsData((prev) => ({ ...prev, [cat.value]: mapped }));
          } catch (err) {
            console.error(
              "Failed to fetch products for category:",
              cat.value,
              err
            );
            setProductsData((prev) => ({ ...prev, [cat.value]: [] }));
          } finally {
            setLoadingProducts((prev) => ({ ...prev, [cat.value]: false }));
          }
        });

        await Promise.all(productFetchPromises);
      } catch (err) {
        console.error("Failed to fetch categories:", err);
      } finally {
        setLoadingCategories(false);
      }
    }

    fetchCategories();
  }, []);

  const displayProducts = useMemo(() => {
    if (!selectedCategory) return [];
    return productsData[selectedCategory] || [];
  }, [productsData, selectedCategory]);

  const isLoadingProducts = loadingProducts[selectedCategory] || false;

  return (
    <div className={classes.section3Wrapper}>
      <div className={classes.headerRow}>
        <div className={classes.titleColumn}>
          <h3 className={classes.sectionTitle}>Popular Product</h3>
        </div>
        {categories.length > 0 && (
          <div className={classes.tabsColumn}>
            <Tabs
              categories={categories}
              selectedCategory={selectedCategory}
              onSelectCategory={setSelectedCategory}
            />
          </div>
        )}
      </div>

      {loadingCategories ? (
        <div className={classes.skeletonWrapper}>
          <ProductCardSkeleton count={4} />
        </div>
      ) : isLoadingProducts ? (
        <div className={classes.skeletonWrapper}>
          <ProductCardSkeleton count={4} />
        </div>
      ) : displayProducts.length > 0 ? (
        <SliderComp slidesToShow={4}>
          {displayProducts.map((product) => (
            <div key={product.id} className={classes.sliderItem}>
              <ProductCard product={product} />
            </div>
          ))}
        </SliderComp>
      ) : (
        <p className="text-muted text-center mb-0">
          No products available at the moment.
        </p>
      )}
    </div>
  );
}
